import {
  acquirePolicyTransactionLock,
  PolicyTransactionLockResultDto,
  releasePolicyTransactionLock,
} from "utils/api/policyTransactionApi";

// Acquire the edit lock on a record. Fires automatically (row expand, Edit
// click) so it stays silent — no toast per acquisition attempt. Acquiring a lock
// you already hold succeeds and extends it, so this is safe to call repeatedly.
// Returns the acquire result so the caller can react if someone else holds it.
export async function lockPolicyRecord(policyTransactionID: number) {
  return acquirePolicyTransactionLock({
    PolicyTransactionID: policyTransactionID,
    LockContext: "IN_EDIT",
  });
}

// Release the edit lock automatically (collapse, unmount, save/action
// buttons). Silent, same reasoning as lockPolicyRecord. Scoped server-side to
// the caller's own lock, so this can never release someone else's.
export async function unlockPolicyRecordSilently(
  policyTransactionID: number
): Promise<boolean> {
  return releasePolicyTransactionLock({
    PolicyTransactionID: policyTransactionID,
  });
}

// ---------------------------------------------------------------------------
// Lock sessions
//
// The drawer ties the lock to the mount/unmount of PolicyDetailsPanel, which
// assumes one mount per expand. StewartTable doesn't guarantee that — it
// reconciles its row list through an unkeyed fragment, swaps the table body's
// element type when `rows.length` crosses 0, and returns null while its column
// state lags the `cols` prop. Any of those tears the expanded details row down
// and rebuilds it, so a single expand could mount the panel twice and fire two
// acquires, then two releases on collapse.
//
// A session decouples the network calls from the mount count: every mount of a
// record joins the same ref-counted session and shares one acquire promise, and
// only the last leaver releases. A plain ref-count isn't enough on its own —
// React runs the old cleanup and the new setup back to back on a remount, so the
// count would hit 0 and return to 1, still producing a release/re-acquire pair.
// The release is therefore deferred by a short grace period and cancelled if the
// record re-registers first.
// ---------------------------------------------------------------------------

type LockSession = {
  refCount: number;
  // Shared across every mount of the same record — a remount awaits this
  // instead of issuing another POST.
  acquire: Promise<PolicyTransactionLockResultDto | null>;
  // Whether the server actually gave us the lock. Set when `acquire` settles;
  // read only after awaiting it, so a release can never overtake its acquire.
  acquired: boolean;
  releaseTimer: ReturnType<typeof setTimeout> | null;
};

const sessions = new Map<number, LockSession>();

// A genuine collapse and a StewartTable remount look identical at the instant
// the cleanup runs, so hold the release long enough for a remount to cancel it.
const RELEASE_GRACE_MS = 250;

// Join (or open) the lock session for a record. The returned promise resolves
// with the acquire result — the same result for every participant, so a remount
// still gets the server's verdict without a second round trip.
export function beginLockSession(
  policyTransactionID: number
): Promise<PolicyTransactionLockResultDto | null> {
  const existing = sessions.get(policyTransactionID);
  if (existing) {
    if (existing.releaseTimer) {
      clearTimeout(existing.releaseTimer);
      existing.releaseTimer = null;
    }
    existing.refCount += 1;
    return existing.acquire;
  }

  const session: LockSession = {
    refCount: 1,
    acquire: Promise.resolve(null),
    acquired: false,
    releaseTimer: null,
  };
  session.acquire = lockPolicyRecord(policyTransactionID).then((result) => {
    session.acquired = !!result?.LockAcquired;
    return result;
  });
  sessions.set(policyTransactionID, session);
  return session.acquire;
}

// Leave the lock session. The last leaver releases, after the grace period and
// only if we ever held the lock — a viewer who was refused never releases the
// holder's lock on their way out.
export function endLockSession(policyTransactionID: number): void {
  const session = sessions.get(policyTransactionID);
  // Already force-released by an action button; nothing left to unwind.
  if (!session) return;

  session.refCount -= 1;
  if (session.refCount > 0) return;

  session.releaseTimer = setTimeout(() => {
    // Drop the entry first: a re-expand after the grace window must open a
    // fresh session rather than adopt this one.
    sessions.delete(policyTransactionID);
    session.acquire.then(() => {
      if (session.acquired) unlockPolicyRecordSilently(policyTransactionID);
    });
  }, RELEASE_GRACE_MS);
}

// Drop the lock now, regardless of who still has the drawer open. For actions
// that finish the editing session while the panel stays mounted (re-run
// validation, force promote, hold, override financials). Tearing the entry down
// means the panel's later endLockSession is a no-op, so the record is released
// exactly once.
export function forceReleaseLockSession(policyTransactionID: number): void {
  const session = sessions.get(policyTransactionID);
  if (session?.releaseTimer) clearTimeout(session.releaseTimer);
  sessions.delete(policyTransactionID);
  unlockPolicyRecordSilently(policyTransactionID);
}

// A pending release is a timer, and timers don't survive a page teardown. Flush
// them on the way out so a collapse-then-navigate-away inside the grace window
// still releases instead of waiting for the server's ExpiresAt.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    sessions.forEach((session, policyTransactionID) => {
      if (!session.releaseTimer) return;
      clearTimeout(session.releaseTimer);
      session.releaseTimer = null;
      if (session.acquired) unlockPolicyRecordSilently(policyTransactionID);
    });
    sessions.clear();
  });
}

// No user-initiated "unlock" helper: the locked banner is only ever shown to a
// user who does NOT hold the lock, and PolicyTransactionLock_ReleaseInEdit only
// releases the caller's own lock. Forcing another user's release needs a new
// stored procedure; until then a stale lock clears via ExpiresAt.
